import { msalInstance, loginRequest, getToken } from "@/auth/authConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function getAuthHeaders() {
  try {
    const account = msalInstance.getActiveAccount();
    if (!account) {
      console.warn("No active account found. Attempting to use first available account.");
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length === 0) {
        console.error("No accounts available. User may not be logged in.");
        return {};
      }
      // Set the first account as active
      msalInstance.setActiveAccount(accounts[0]);
    }
    
    try {
      const response = await getToken();
      if (!response) {
        console.error("getToken() returned empty response");
        return {};
      }
      console.log("Token acquired successfully");
      return { "oidc-token": response };
    } catch (tokenError) {
      console.error("Token acquisition failed:", tokenError);
      return {};
    }
  } catch (error) {
    console.error("Error in getAuthHeaders:", error);
    return {};
  }
}

async function request(url, options = {}) {
  const authHeaders = await getAuthHeaders();
  
  if (!authHeaders["oidc-token"]) {
    console.warn("No auth token available for request to:", url);
  }
  
  const res = await fetch(url, {
    headers: { 
      'Content-Type': 'application/json', 
      ...authHeaders, 
      ...options.headers 
    },
    ...options,
  });
  
  if (!res.ok) {
    const err = await res.text();
    console.error(`API request failed: ${res.status} ${res.statusText}`, err);
    throw new Error(err || res.statusText);
  }
  
  return res.json();
}

async function authedFetch(url, options = {}) {
  const authHeaders = await getAuthHeaders();
  
  if (!authHeaders["oidc-token"]) {
    console.warn("No auth token available for request to:", url);
  }
  
  return fetch(url, { 
    ...options, 
    headers: { ...authHeaders, ...options.headers } 
  });
}

export const api = {
  getProjects: () => request(`${API}/projects`),
  getProject: (id) => request(`${API}/projects/${id}`),
  getNextId: () => request(`${API}/projects/next-id`).then(d => d.id),
  createProject: (project) => request(`${API}/projects`, { method: 'POST', body: JSON.stringify(project) }),
  updateProject: (id, project) => request(`${API}/projects/${id}`, { method: 'PUT', body: JSON.stringify(project) }),
  deleteProject: (id) => authedFetch(`${API}/projects/${id}`, { method: 'DELETE' }),
  lockProject: (id, passkey) => request(`${API}/projects/${id}/lock`, { method: 'POST', body: JSON.stringify({ passkey }) }),
  unlockProject: (id, passkey) => request(`${API}/projects/${id}/unlock`, { method: 'POST', body: JSON.stringify({ passkey }) }),
  saveActualResources: (id, actualResources) => request(`${API}/projects/${id}/actual-resources`, { method: 'PUT', body: JSON.stringify({ actualResources }) }),

  getAdhocInvoices: () => request(`${API}/invoices/adhoc`),
  createAdhocInvoice: (inv) => request(`${API}/invoices/adhoc`, { method: 'POST', body: JSON.stringify(inv) }),
  updateAdhocInvoice: (id, inv) => request(`${API}/invoices/adhoc/${id}`, { method: 'PUT', body: JSON.stringify(inv) }),
  deleteAdhocInvoice: (id) => authedFetch(`${API}/invoices/adhoc/${id}`, { method: 'DELETE' }),

  getNotes: (monthKey) => request(`${API}/notes/${monthKey}`),
  saveNotes: (monthKey, data) => request(`${API}/notes/${monthKey}`, { method: 'PUT', body: JSON.stringify(data) }),

  // Analytics endpoints (backend pre-calculates all metrics)
  analyticsHealth: () => request(`${API}/analytics/health`),
  monthlyInvoiceSummary: (monthKey) => {
    // Convert format from "2025-01" to "202501" for backend
    const normalized = typeof monthKey === 'string' ? monthKey.replace('-', '') : monthKey;
    return request(`${API}/analytics/monthly-summary/${normalized}`);
  },
  utilizationMetrics: () => request(`${API}/analytics/utilization-metrics`),
  projectProfitability: () => request(`${API}/analytics/project-profitability`),
  dashboardKPIs: () => request(`${API}/analytics/dashboard-kpis`),
  consumptionTrend: (projectId) => request(`${API}/analytics/consumption-trend/${projectId}`),

  // Master Data endpoints
  // Clients
  listClients: (activeOnly = true) => request(`${API}/master-data/clients?active_only=${activeOnly}`),
  getClient: (clientId) => request(`${API}/master-data/clients/${clientId}`),
  createClient: (client) => request(`${API}/master-data/clients`, { method: 'POST', body: JSON.stringify(client) }),
  updateClient: (clientId, client) => request(`${API}/master-data/clients/${clientId}`, { method: 'PUT', body: JSON.stringify(client) }),
  deleteClient: (clientId) => authedFetch(`${API}/master-data/clients/${clientId}`, { method: 'DELETE' }),

  // Project Leads
  listProjectLeads: (activeOnly = true) => request(`${API}/master-data/project-leads?active_only=${activeOnly}`),
  getProjectLead: (leadId) => request(`${API}/master-data/project-leads/${leadId}`),
  createProjectLead: (lead) => request(`${API}/master-data/project-leads`, { method: 'POST', body: JSON.stringify(lead) }),
  updateProjectLead: (leadId, lead) => request(`${API}/master-data/project-leads/${leadId}`, { method: 'PUT', body: JSON.stringify(lead) }),
  deleteProjectLead: (leadId) => authedFetch(`${API}/master-data/project-leads/${leadId}`, { method: 'DELETE' }),

  // Project Types
  listProjectTypes: (activeOnly = true) => request(`${API}/master-data/project-types?active_only=${activeOnly}`),
  getProjectType: (typeId) => request(`${API}/master-data/project-types/${typeId}`),
  createProjectType: (ptype) => request(`${API}/master-data/project-types`, { method: 'POST', body: JSON.stringify(ptype) }),
  updateProjectType: (typeId, ptype) => request(`${API}/master-data/project-types/${typeId}`, { method: 'PUT', body: JSON.stringify(ptype) }),
  deleteProjectType: (typeId) => authedFetch(`${API}/master-data/project-types/${typeId}`, { method: 'DELETE' }),

  // Categories
  listCategories: (activeOnly = true) => request(`${API}/master-data/categories?active_only=${activeOnly}`),
  getCategory: (categoryId) => request(`${API}/master-data/categories/${categoryId}`),
  createCategory: (category) => request(`${API}/master-data/categories`, { method: 'POST', body: JSON.stringify(category) }),
  updateCategory: (categoryId, category) => request(`${API}/master-data/categories/${categoryId}`, { method: 'PUT', body: JSON.stringify(category) }),
  deleteCategory: (categoryId) => authedFetch(`${API}/master-data/categories/${categoryId}`, { method: 'DELETE' }),

  // Users
  listUsers: () => request(`${API}/users`),

  // Documents
  uploadDocument: async (file, projectId, phase) => {
    const authHeaders = await getAuthHeaders();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('project_id', projectId);
    formData.append('phase', phase);
    return fetch(`${API}/documents/upload`, {
      method: 'POST',
      headers: authHeaders,
      body: formData,
    }).then(res => {
      if (!res.ok) throw new Error(res.statusText);
      return res.json();
    });
  },
  deleteDocument: (blobName, projectId) => authedFetch(`${API}/documents/delete?blob_name=${encodeURIComponent(blobName)}&project_id=${encodeURIComponent(projectId)}`, { method: 'DELETE' }),
};
